import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import * as React from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { UserEditIcon } from '@/components/icons/UserEditIcon';
import {
  TrackUserEventModule,
  useTrackUserEvent,
} from '@/hooks/useTrackUserEvent';
import { COLORS } from '@/styles/tokens/colors';

import { ActivityItem, ActivityItemProps } from './ActivityItem';

export interface NotificationTrayActivityItem extends ActivityItemProps {
  id: string;
}

export interface ActivitiesTabProps {
  activities: NotificationTrayActivityItem[];
  onClearCompleted: () => void;
  /** Called after the user follows a link out of the tray, such as a household link. */
  onNavigate?: () => void;
  loadOlder?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

export function ActivitiesTab({
  activities,
  onClearCompleted,
  onNavigate,
  loadOlder,
  hasMore = false,
  loadingMore = false,
}: ActivitiesTabProps) {
  const trackUserEvent = useTrackUserEvent();
  const isEmpty = activities.length === 0;
  const hasCompletedActivities = activities.some(
    (activity) =>
      activity.status === 'completed' || activity.status === 'failed'
  );

  const handleClearCompleted = () => {
    trackUserEvent(
      `${TrackUserEventModule.NOTIFICATION} cleared completed activities`,
      {
        activity_count: activities.length,
      }
    );
    onClearCompleted();
  };

  const createHouseholdClickHandler =
    (activity: NotificationTrayActivityItem) => () => {
      trackUserEvent(
        `${TrackUserEventModule.NOTIFICATION} activity household clicked`,
        {
          activity_id: activity.id,
          activity_status: activity.status,
        }
      );
      onNavigate?.();
    };

  const debouncedLoadOlder = useMemo(
    () =>
      debounce((scrollPosition: number) => {
        // Load more when user is within 200px of the bottom
        if (scrollPosition < 200 && hasMore && !loadingMore && loadOlder) {
          loadOlder();
        }
      }, 150),
    [hasMore, loadingMore, loadOlder]
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (!target) return;

    const scrollPosition =
      target.scrollHeight - target.scrollTop - target.clientHeight;

    debouncedLoadOlder(scrollPosition);
  };

  if (isEmpty) {
    return (
      <Stack
        spacing={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          px: 2,
        }}
      >
        <UserEditIcon size={30} color={COLORS.GRAY[300]} />
        <Typography variant="title-l" component="span">
          No recent activities
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ height: '100%' }}>
      {/* Content */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
        onScroll={loadOlder ? handleScroll : undefined}
      >
        <Box
          sx={{
            position: 'relative',
            '&::before':
              activities.length > 1
                ? {
                    // show the line behind all of the items connecting them
                    content: '""',
                    position: 'absolute',
                    left: '36px', // Center of the 40px icon (16px padding + 20px center)
                    top: '30px', // Start behind the first icon
                    bottom: '30px', // End behind the last icon
                    width: '2px',
                    backgroundColor: COLORS.GRAY[200],
                    zIndex: 0,
                  }
                : {},
          }}
        >
          <Stack spacing={0}>
            {activities.map((activity) => (
              <ActivityItem
                key={activity.id}
                {...activity}
                onHouseholdClick={createHouseholdClickHandler(activity)}
              />
            ))}
          </Stack>
        </Box>

        {/* Loading indicator */}
        {loadingMore && (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={16} />
          </Stack>
        )}
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        p={1.5}
        spacing={1}
        sx={{
          borderTop: `1px solid ${COLORS.GRAY[200]}`,
        }}
      >
        <Button
          disabled={!hasCompletedActivities}
          variant="secondary"
          size="sm"
          onClick={handleClearCompleted}
        >
          Clear completed activities
        </Button>
      </Stack>
    </Stack>
  );
}
